import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { movies } from "@/mocks/movies";
import { genres } from "@/mocks/genres";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { useToast } from "@/components/feature/ToastProvider";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MovieGrid from "@/components/feature/MovieGrid";
import PlayerDemo from "./components/PlayerDemo";

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const { showToast } = useToast();
  const movie = movies.find((m) => m.id === id);

  useDocumentTitle(
    movie
      ? `${movie.title}（${movie.year}） - 电影详情 - 鸭王在线观看`
      : "电影详情 - 鸭王在线观看",
    movie
      ? `${movie.title}（${movie.year}）电影详情：${movie.logline}`
      : "鸭王在线观看电影详情页。"
  );

  const related = useMemo(() => {
    if (!movie) return [];
    return movies
      .filter((m) => m.id !== movie.id && m.genres.some((g) => movie.genres.includes(g)))
      .slice(0, 6);
  }, [movie]);

  if (!movie) {
    return (
      <div className="mx-auto flex w-full max-w-[1280px] flex-col items-center gap-4 px-4 py-24 text-center md:px-6">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-3xl text-foreground-500">
          <i className="ri-film-line"></i>
        </span>
        <h1 className="font-heading text-2xl font-bold text-foreground-950">影片不存在</h1>
        <p className="text-sm text-foreground-600">你访问的影片可能已下线，去看看其他影片吧。</p>
        <Link
          to="/"
          className="cursor-pointer whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-background-950 transition-colors hover:bg-accent-400"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const primaryGenre = genres.find((g) => g.name === movie.genres[0]);

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-6 md:px-6 md:py-10">
      <Breadcrumb
        items={[
          { label: `${movie.genres[0]}电影`, to: primaryGenre ? `/category/${primaryGenre.slug}` : "/" },
          { label: movie.title },
        ]}
      />

      <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-[340px_1fr]">
        <div>
          <div
            className="relative w-full overflow-hidden rounded-lg border border-background-200/60"
            style={{ aspectRatio: "2 / 3" }}
          >
            <img
              src={movie.poster}
              alt={`${movie.title}（${movie.year}）电影海报`}
              title={`${movie.title} - 鸭王在线观看`}
              className="h-full w-full object-cover object-top"
            />
            <button
              type="button"
              onClick={() => showToast("当前为演示状态，播放功能待接入")}
              className="absolute inset-0 flex cursor-pointer items-center justify-center bg-background-950/30 opacity-100 transition-opacity hover:bg-background-950/50"
              aria-label="播放"
            >
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-accent-500 text-background-950">
                <i className="ri-play-fill text-3xl"></i>
              </span>
            </button>
          </div>
        </div>

        <div className="flex flex-col">
          <h1 className="font-heading text-3xl font-extrabold text-foreground-950 md:text-4xl">
            {movie.title}
          </h1>
          <p className="mt-1 text-sm text-foreground-600">
            {movie.year} 年 · {movie.region}
          </p>

          <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-foreground-700">
            <span className="flex items-center gap-1">
              <i className="ri-star-fill text-accent-500"></i>
              <span className="font-semibold text-accent-500">{movie.rating.toFixed(1)}</span>
              <span className="text-foreground-600">分</span>
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line text-primary-300"></i>
              {movie.duration}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-fire-line text-accent-500"></i>
              {(movie.views / 10000).toFixed(1)} 万次观看
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line text-primary-300"></i>
              更新于 {movie.updatedAt}
            </span>
          </div>

          <div className="mt-5 flex flex-wrap gap-2">
            {movie.genres.map((g) => {
              const item = genres.find((gg) => gg.name === g);
              return (
                <Link
                  key={g}
                  to={item ? `/category/${item.slug}` : "/"}
                  className="flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-full bg-secondary-500/25 px-4 py-1.5 text-sm text-foreground-900 transition-colors hover:bg-secondary-500/40"
                >
                  <i className="ri-price-tag-3-line text-base"></i>
                  {g}
                </Link>
              );
            })}
          </div>

          <div className="mt-6 rounded-lg border border-background-200/60 bg-background-100 p-5">
            <h2 className="font-heading text-lg font-bold text-foreground-950">剧情简介</h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-700">{movie.logline}</p>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">{movie.synopsis}</p>
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => showToast("当前为演示状态，播放功能待接入")}
              className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-background-950 transition-colors hover:bg-accent-400"
            >
              <i className="ri-play-fill text-lg"></i>
              立即播放
            </button>
            <button
              type="button"
              onClick={() => showToast("收藏功能演示中，待接入")}
              className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300/60 px-6 py-3 text-sm font-medium text-foreground-900 transition-colors hover:border-accent-500 hover:text-accent-500"
            >
              <i className="ri-bookmark-line text-lg"></i>
              收藏
            </button>
            <button
              type="button"
              onClick={() => showToast("分享功能演示中，待接入")}
              className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300/60 px-6 py-3 text-sm font-medium text-foreground-900 transition-colors hover:border-accent-500 hover:text-accent-500"
            >
              <i className="ri-share-forward-line text-lg"></i>
              分享
            </button>
          </div>
        </div>
      </div>

      <PlayerDemo movie={movie} />

      {related.length > 0 && (
        <section className="mt-10 md:mt-14">
          <div className="mb-4 flex items-center gap-3">
            <span className="h-6 w-1.5 rounded-full bg-accent-500"></span>
            <h2 className="font-heading text-xl font-bold text-foreground-950">相关推荐</h2>
          </div>
          <MovieGrid movies={related} />
        </section>
      )}
    </div>
  );
}